/**
 * My Stores — saved feed/seed stores from localStorage. Shown above the store finder on Feed Management and Area Calculator.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { FaBookmark } from 'react-icons/fa';
import { getSavedStores, removeSavedStore, storeId } from '../../../utils/savedStoresStorage';
import { YCard } from '../../../ui/YCard';
import { YButton } from '../../../ui/YButton';
import './MyStoresCard.css';

function ShellV2StoreCard({ store, onRemove, shell }) {
  const address = store.address || [store.city, store.state].filter(Boolean).join(', ');
  const directionsUrl = store.urls?.directions
    || (store.coordinates
      ? `https://www.google.com/maps/dir/?api=1&destination=${store.coordinates.latitude},${store.coordinates.longitude}`
      : null);

  return (
    <article className={`${shell}-store-card`}>
      <div className={`${shell}-store-card__top`}>
        <div className={`${shell}-store-card__title-row`}>
          <h3 className={`${shell}-store-card__name`}>{store.name || 'Store'}</h3>
          <FaBookmark className={`${shell}-store-card__pin`} aria-hidden="true" />
        </div>
        {address ? <p className={`${shell}-store-card__meta`}>{address}</p> : null}
      </div>
      <div className={`${shell}-store-card__actions`}>
        {directionsUrl && (
          <a className="y-btn y-btn-secondary y-btn-sm" href={directionsUrl} target="_blank" rel="noreferrer">
            Directions
          </a>
        )}
        {store.urls?.website && (
          <a className="y-btn y-btn-secondary y-btn-sm" href={store.urls.website} target="_blank" rel="noreferrer">
            Website
          </a>
        )}
        <YButton
          variant="secondary"
          size="sm"
          onClick={() => onRemove(store)}
          aria-label={`Remove ${store.name} from My Stores`}
        >
          Remove
        </YButton>
      </div>
    </article>
  );
}

const SHELL_V2_PREFIX = {
  'plant-asst': 'plant-asst',
  'feed-mgmt': 'feed-mgmt',
};

export function MyStoresCard({ appearance = 'default' }) {
  const [stores, setStores] = useState([]);

  const load = useCallback(async () => {
    const list = await getSavedStores();
    setStores(list);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onUpdate = () => load();
    window.addEventListener('yona-my-stores-updated', onUpdate);
    return () => window.removeEventListener('yona-my-stores-updated', onUpdate);
  }, [load]);

  const handleRemove = useCallback(
    async (store) => {
      const id = typeof store.id === 'number' ? store.id : storeId(store);
      await removeSavedStore(id);
      load();
    },
    [load]
  );

  const shellPrefix = SHELL_V2_PREFIX[appearance];
  if (shellPrefix) {
    const emptyClass =
      shellPrefix === 'feed-mgmt'
        ? 'feed-mgmt-stores-inline-empty feed-mgmt-stores-inline-empty--panel'
        : 'plant-asst-inline-empty';
    if (stores.length === 0) {
      return (
        <p className={emptyClass}>
          Saved stores will appear here. Search below and use &quot;Save to My Stores&quot; on any result.
        </p>
      );
    }

    return (
      <div className={`${shellPrefix}-store-grid`} data-testid={`${shellPrefix}-my-stores-grid`}>
        {stores.map((store) => (
          <ShellV2StoreCard
            key={store.store_id || storeId(store)}
            store={store}
            onRemove={handleRemove}
            shell={shellPrefix}
          />
        ))}
      </div>
    );
  }

  if (stores.length === 0) {
    return (
      <YCard headerVariant="muted" title="My Stores">
        <p className="text-muted small mb-0">
          Saved stores will appear here. Search below and use &quot;Save to My Stores&quot; on any result.
        </p>
      </YCard>
    );
  }

  return (
    <YCard headerVariant="muted" title="My Stores">
      <div className="my-stores-list">
        {stores.map((store) => (
          <div key={store.store_id || storeId(store)} className="my-stores-item">
            <div className="my-stores-item__main">
              <div className="my-stores-item__name">{store.name || 'Store'}</div>
              <div className="text-muted small">
                {store.address || [store.city, store.state].filter(Boolean).join(', ')}
              </div>
            </div>
            <div className="my-stores-item__actions">
              {(store.urls?.directions || store.coordinates) && (
                <a
                  className="y-btn y-btn-ghost y-btn-sm"
                  href={
                    store.urls?.directions
                    || `https://www.google.com/maps/dir/?api=1&destination=${store.coordinates.latitude},${store.coordinates.longitude}`
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  Directions
                </a>
              )}
              {store.urls?.website && (
                <a className="y-btn y-btn-ghost y-btn-sm" href={store.urls.website} target="_blank" rel="noreferrer">
                  Website
                </a>
              )}
              <button
                type="button"
                className="y-btn y-btn-ghost y-btn-sm text-muted"
                onClick={() => handleRemove(store)}
                aria-label={`Remove ${store.name} from My Stores`}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </YCard>
  );
}
